import {
  Association,
  CommunityAssociation,
  Group,
  Opportunity,
  OpportunityParticipant,
  OrganizationManager,
  TimeReport,
  User,
} from "@entities";
import { createControllerHook } from "@hooks";
import { Roles } from "@shared/Roles";
import { HyperBackendMethod } from "@shared/backend/hyper-backend-method";
import dayjs from "dayjs";
import type { EntityFilter } from "remult";
import { Controller, remult, repo } from "remult";
import * as XLSX from "xlsx";
import { GENDER_TO_HEB, OPPORTUNITY_PATH, WEME_APP_BASE_URL } from "@shared/helpers/constants";
import { resolveParticipantMatchWeight } from "@shared/helpers/match-weight";

type ExportRow = Record<string, string | number>;

type PreparedExcelDownload = {
  data: string;
  filename: string;
  contentType: string;
  isBase64: true;
};

@Controller("excel")
export class ExcelController {
  init(config: Record<string, any>): void {
    // Initialize the controller with the provided configuration
    Object.assign(this, config);
  }
  /**
   * Export users data to Excel
   * @param whereToUpdate - EntityFilter to specify which users to export
   * @param name - Optional filename prefix
   */
  @HyperBackendMethod({
    allowed: Roles.AdminsAndManager,
    overrideSecurity: true,
  })
  static async exportUsersToExcel(
    whereToUpdate: EntityFilter<Association>,
    name?: string,
  ) {
    // Fetch users based on the provided filter
    const selectedUsers = await repo(Association).find({
      where: whereToUpdate,
      include: {
        user: true,
      },
    });

    // Map user data to fit the required column headers
    const users = selectedUsers.map((association) => ({
      "שם מלא": association.user?.name || "",
      "ת.ז": association.user?.numberID || "",
      "מספר טלפון": association.user?.phoneNumber || "",
      כתובת: association.user?.address || "",
      "תאריך הרשמה למערכת": new Date(
        association.user?.createdAt || "",
      ).toLocaleDateString("he"),
      הערות: association.comments,
    }));

    const columnWidths = ExcelController.getColumnWidths(users);
    const filenameString = name || "volunteers";
    return ExcelController.prepareExcelForDownload(
      users,
      columnWidths,
      filenameString,
    );
  }

  /**
   * Export groups data to Excel
   * @param whereToUpdate - EntityFilter to specify which groups to export
   */
  @HyperBackendMethod({
    allowed: Roles.AdminsAndManager,
    overrideSecurity: true,
  })
  static async exportGroupsToExcel(whereToUpdate: EntityFilter<Group>) {
    // Fetch groups based on the provided filter
    const selectedGroups = await repo(Group).find({
      where: whereToUpdate,
      include: {
        admin: true,
      },
    });

    // Map group data to fit the required column headers
    const groups = selectedGroups.map((group) => ({
      "שם הקבוצה": group.name,
      "גודל הקבוצה": group.size,
      תיאור: group.description,
      "פרטי המוביל/ה": group.admin
        ? `${group.admin?.name} , ${group.admin?.phoneNumber}`
        : "-",
      "זמנים מועדפים":
        group.preferredStartDate || group.preferredEndDate
          ? `${group.preferredStartDate ? new Date(group.preferredStartDate).toLocaleDateString("he") + " - " : ""}${group.preferredEndDate ? new Date(group.preferredEndDate).toLocaleDateString("he") : ""} `
          : "-",
    }));

    const columnWidths = ExcelController.getColumnWidths(groups);
    const filenameString = "groups";
    return ExcelController.prepareExcelForDownload(
      groups,
      columnWidths,
      filenameString,
    );
  }

  /**
   * Export opportunity participants data to Excel
   * @param whereToUpdate - EntityFilter to specify which opportunity participants to export
   */
  @HyperBackendMethod({
    allowed: Roles.AdminsAndManager,
    overrideSecurity: true,
  })
  static async exportParticipateInOpportunitiesToExcel(
    whereToUpdate: EntityFilter<OpportunityParticipant>,
  ): Promise<PreparedExcelDownload | null> {
    return ExcelController.prepareParticipateInOpportunitiesExcel(
      whereToUpdate,
      "Participate-In-Opportunities",
    );
  }

  /**
   * Same columns as {@link exportParticipateInOpportunitiesToExcel}, for pending / "interested" rows.
   */
  @HyperBackendMethod({
    allowed: Roles.AdminsAndManager,
    overrideSecurity: true,
  })
  static async exportInterestedInOpportunitiesToExcel(
    whereToUpdate: EntityFilter<OpportunityParticipant>,
  ): Promise<PreparedExcelDownload | null> {
    return ExcelController.prepareParticipateInOpportunitiesExcel(
      whereToUpdate,
      "Interested-In-Opportunities",
    );
  }

  private static async prepareParticipateInOpportunitiesExcel(
    whereToUpdate: EntityFilter<OpportunityParticipant>,
    filenamePrefix: string,
  ): Promise<PreparedExcelDownload | null> {
    const selectedEventParticipant = await repo(OpportunityParticipant).find({
      where: whereToUpdate,
      include: {
        opportunity: true,
        association: {
          include: {
            user: true,
          },
        },
        group: true,
      },
    });

    if (selectedEventParticipant.length === 0) return null;

    const eventParticipant = selectedEventParticipant.map((opportunity) => ({
      "שם המשימה": opportunity.opportunity?.title ?? "",
      "תאריך התחלה": opportunity.opportunity?.startDate
        ? new Date(opportunity.opportunity?.startDate).toLocaleDateString("he")
        : "",
      "תאריך סיום": opportunity.opportunity?.endDate
        ? new Date(opportunity.opportunity?.endDate).toLocaleDateString("he")
        : "",
      "תאריך יצירה": opportunity.createdAt
        ? new Date(opportunity.createdAt).toLocaleDateString("he")
        : "-",
      "שם המתנדב": opportunity.association?.user?.name ?? "",
      "שיוך לקבוצות": opportunity.group?.name ?? "",
      "מספר טלפון": opportunity.association?.user?.phoneNumber ?? "",
      "ת.ז": opportunity.association?.user?.numberID ?? "",
    }));

    const columnWidths = ExcelController.getColumnWidths(eventParticipant);
    return ExcelController.prepareExcelForDownload(
      eventParticipant,
      columnWidths,
      filenamePrefix,
    );
  }

  /**
   * Export time reports data to Excel
   * @param whereToUpdate - EntityFilter to specify which time reports to export
   */
  @HyperBackendMethod({
    allowed: Roles.AdminsAndManager,
    overrideSecurity: true,
  })
  static async exportTimeReportsToExcel(
    whereToUpdate: EntityFilter<TimeReport>,
  ) {
    // Fetch time reports based on the provided filter
    const selectedTimeReports = await repo(TimeReport).find({
      where: whereToUpdate,
      include: {
        association: {
          include: {
            user: true,
          },
        },
        organizationManager: true,
      },
    });

    // Map time report data to fit the required column headers
    const timeReports = selectedTimeReports.map((timeReport) => ({
      "שם המדווח/ת": timeReport.association?.user?.name ?? "",
      "מספר טלפון": timeReport.association?.user?.phoneNumber ?? "",
      "תאריך התנדבות": timeReport.date
        ? new Date(timeReport.date).toLocaleDateString("he")
        : "",
      "מספר שעות": timeReport.hourCount ?? "",
      "מנהל/ת": timeReport.organizationManager?.name ?? "",
      "האם אושר": timeReport.isConfirmed === true ? "אושר" : "לא אושר",
      הערות: timeReport.comments ?? "",
    }));

    const columnWidths = ExcelController.getColumnWidths(timeReports);
    const filenameString = "Time-Reports";
    return ExcelController.prepareExcelForDownload(
      timeReports,
      columnWidths,
      filenameString,
    );
  }

  /**
   * Export organization managers data to Excel
   * @param whereToUpdate - EntityFilter to specify which managers to export
   */
  @HyperBackendMethod({
    allowed: Roles.AdminsAndManager,
    overrideSecurity: true,
  })
  static async exportManagersToExcel(
    whereToUpdate: EntityFilter<OrganizationManager>,
  ) {
    // Fetch managers based on the provided filter
    const selectedManagers = await repo(OrganizationManager).find({
      where: whereToUpdate,
    });

    // Map manager data to fit the required column headers
    const managers = selectedManagers.map((manager) => ({
      "שם מלא": manager.name ?? "",
      תפקיד: manager.role ?? "",
      "מספר טלפון": manager.phoneNumber ?? "",
      "דוא״ל": manager.email ?? "",
    }));

    const columnWidths = ExcelController.getColumnWidths(managers);
    const filenameString = "managers";
    return ExcelController.prepareExcelForDownload(
      managers,
      columnWidths,
      filenameString,
    );
  }

  /**
   * Export opportunities data to Excel
   * @param whereToUpdate - EntityFilter to specify which opportunities to export
   */
  @HyperBackendMethod({
    allowed: Roles.AdminsAndManager,
    overrideSecurity: true,
  })
  static async exportOpportunitiesToExcel(
    whereToUpdate: EntityFilter<Opportunity>,
  ) {
    // Fetch opportunities based on the provided filter
    const selectedOpportunities = await repo(Opportunity).find({
      where: whereToUpdate,
      include: {
        manager: true,
        progressStatus: true,
        associationRequested: { include: { user: true } },
      },
    });

    function formatPhoneNumber(phoneNumber?: string) {
      if (!phoneNumber) return "-";
      let formattedVal = phoneNumber.replace("+972", "0");

      // Format the phone number as XXX-XXX-XXXXX
      formattedVal = formattedVal.replace(/^(\d{3})(\d{3})(\d+)$/, "$1-$2-$3");

      return formattedVal;
    }

    // Map opportunity data to fit the required column headers
    const opportunities = selectedOpportunities.map((opportunity) => {
      const volunteerLink = `${WEME_APP_BASE_URL}${OPPORTUNITY_PATH}/${opportunity.id}`;
      return {
        כותרת: opportunity.title ?? "",
        תיאור: opportunity.description ?? "",
        "תאריך התחלה": opportunity.startDate
          ? new Date(opportunity.startDate).toLocaleDateString("he")
          : "",
        "תאריך סיום": opportunity.endDate
          ? new Date(opportunity.endDate).toLocaleDateString("he")
          : "",
        "תיאור זמן": opportunity.timeDescription ?? "",
        מיקום: opportunity.location ?? "",
        "כמות דרושה": opportunity.amount ?? 0,
        "משתתפים נוכחיים": opportunity.participantsCount ?? 0,
        סטטוס: opportunity.progressStatus?.title ?? "",
        "אחראי משימה": opportunity.manager?.name ?? "",
        הערות: opportunity.comments ?? "",
        "פרטי איש קשר": opportunity.contactDetails
          ? `${opportunity.contactDetails.name || ""}, ${opportunity.contactDetails.phoneNumber || ""}, ${opportunity.contactDetails.position || ""}`
          : "",
        "פרטי מבקש סיוע": opportunity.associationRequested
          ? `${opportunity.associationRequested.userName || ""}, ${formatPhoneNumber(opportunity.associationRequested.user?.phoneNumber) || ""}`
          : "",
        "קישור למשימה למתנדב": volunteerLink,
      };
    });

    const columnWidths = ExcelController.getColumnWidths(opportunities);
    const filenameString = "opportunities";
    return ExcelController.prepareExcelForDownload(
      opportunities,
      columnWidths,
      filenameString,
    );
  }

  /**
   * Export aid related users data to Excel
   * @param whereToUpdate - EntityFilter to specify which aid related users to export
   */
  @HyperBackendMethod({
    allowed: Roles.AdminsAndManager,
    overrideSecurity: true,
  })
  static async exportAidRelatedUsersToExcel(whereToUpdate: EntityFilter<User>) {
    // Fetch aid related users based on the provided filter
    const selectedAidRelatedUsers = await repo(User).find({
      where: whereToUpdate,
    });

    // Map aid related user data to fit the required column headers
    const aidRelatedUsers = selectedAidRelatedUsers.map((aidRelatedUser) => ({
      "שם מלא": aidRelatedUser.name ?? "",
      "ת.ז": aidRelatedUser.numberID ?? "",
      כתובת: aidRelatedUser.address ?? "",
      "מספר טלפון": aidRelatedUser.phoneNumber ?? "",
      "תאריך הרשמה למערכת": new Date(
        aidRelatedUser.createdAt || "",
      ).toLocaleDateString("he"),
      מגדר: aidRelatedUser.gender ?? "",
      גיל: (() => {
        if (!aidRelatedUser.dateOfBirth) return "-";
        const birthYear = new Date(aidRelatedUser.dateOfBirth)?.getFullYear();
        const thisYear = new Date().getFullYear();
        return birthYear ? thisYear - birthYear : "-";
      })(),
    }));

    const columnWidths = ExcelController.getColumnWidths(aidRelatedUsers);
    const filenameString = "Aid-Related-Users";
    return ExcelController.prepareExcelForDownload(
      aidRelatedUsers,
      columnWidths,
      filenameString,
    );
  }

  @HyperBackendMethod({
    allowed: Roles.AdminsAndManager,
    overrideSecurity: true,
  })
  static async exportCommunityMembersToExcel(
    whereToUpdate: EntityFilter<CommunityAssociation>,
    communityName?: string,
    kind: "members" | "activity" = "members",
  ): Promise<PreparedExcelDownload> {
    const organizationId = remult.user?.organizationId;
    if (!organizationId) {
      throw new Error("Missing organization");
    }

    const members = await findAllPages((page) =>
      repo(CommunityAssociation).find({
        where: {
          $and: [
            whereToUpdate,
            CommunityAssociation.hyperFilter({
              community: { $is: { organizationId } },
              association: {
                $is: { user: { $is: { isDeleted: false } } },
              },
            }),
          ],
        },
        include: {
          association: { include: { user: true } },
        },
        limit: EXCEL_PAGE_SIZE,
        page,
        orderBy: { createdAt: "asc" },
      }),
    );

    const associationIds = members.map((member) => member.associationId);
    const safeName = safeExportName(communityName);

    if (kind === "activity") {
      const activityRows = await loadCommunityActivityRows(associationIds, members);
      return ExcelController.prepareMultiSheetExcel(
        [
          {
            name: "פעילות",
            headers: ACTIVITY_EXPORT_HEADERS,
            rows: activityRows,
          },
        ],
        `פעילות-קהילה-${safeName}`,
      );
    }

    const activityByAssociation = await loadCommunityMemberActivity(associationIds);
    const memberRows = members.map((member) => {
      const user = member.association?.user;
      const activity = activityByAssociation.get(member.associationId);
      const gender = user?.gender;
      return {
        "שם מלא": user?.name || "",
        "מספר טלפון": user?.phoneNumber || "",
        אימייל: user?.email || "",
        מגדר: gender ? GENDER_TO_HEB[gender] || gender : "",
        גיל: formatExportAge(user?.dateOfBirth),
        "תאריך הצטרפות לקהילה": formatExportDate(member.createdAt),
        "סטטוס פעילות":
          ACTIVITY_STATUS_LABELS[member.association?.activityStatus ?? ""] ||
          member.association?.activityStatus ||
          "",
        "מוביל קהילה": member.isLeader ? "כן" : "לא",
        "מספר התנדבויות מאושרות": activity?.approvedCount ?? 0,
        "תאריך התנדבות אחרון": formatExportDate(activity?.lastAt),
        "שעות מדווחות": activity?.hours ?? 0,
      };
    });

    return ExcelController.prepareMultiSheetExcel(
      [
        {
          name: "חברים",
          headers: MEMBER_EXPORT_HEADERS,
          rows: memberRows,
        },
      ],
      `חברי-קהילה-${safeName}`,
    );
  }

  /**
   * Prepare Excel file for download - returns the data and headers needed by the client
   */
  private static prepareExcelForDownload(
    columnsData: ExportRow[],
    columnWidths: { width: number; }[],
    filenameString: string,
  ): PreparedExcelDownload {
    // Create a worksheet with explicit column headers
    const worksheet = XLSX.utils.json_to_sheet(columnsData);
    // Apply column widths to the worksheet
    worksheet["!cols"] = columnWidths;
    // Create a new workbook and append the worksheet
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "table");
    // Format the date using dayjs in dd/mm/yyyy format
    const formattedDate = dayjs().format("DD/MM/YYYY");
    // Generate filename with the formatted date
    const filename = `${filenameString}-${formattedDate}.xlsx`;

    // Convert to binary buffer for sending to client
    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "base64",
    });

    // Return both the base64 encoded data and the filename
    return {
      data: excelBuffer,
      filename,
      contentType:
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      isBase64: true,
    };
  }

  private static prepareMultiSheetExcel(
    sheets: { name: string; rows: ExportRow[]; headers: string[] }[],
    filenameString: string,
  ): PreparedExcelDownload {
    const workbook = XLSX.utils.book_new();
    for (const sheet of sheets) {
      const worksheet = sheet.rows.length
        ? XLSX.utils.json_to_sheet(sheet.rows, { header: sheet.headers })
        : XLSX.utils.aoa_to_sheet([sheet.headers]);
      const widthSource = sheet.rows.length
        ? sheet.rows
        : [Object.fromEntries(sheet.headers.map((header) => [header, header]))];
      worksheet["!cols"] = ExcelController.getColumnWidths(widthSource);
      XLSX.utils.book_append_sheet(workbook, worksheet, sheet.name);
    }
    const formattedDate = dayjs().format("DD-MM-YYYY");
    const filename = `${filenameString}-${formattedDate}.xlsx`;
    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "base64",
    });
    return {
      data: excelBuffer,
      filename,
      contentType:
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      isBase64: true,
    };
  }

  /**
   * Determine column widths for Excel export
   */
  private static getColumnWidths(
    columnsData: ExportRow[],
  ): { width: number; }[] {
    if (!columnsData.length) return [];
    const headers = Object.keys(columnsData[0]);
    return headers.map((header) => {
      const maxLength = Math.max(
        header.length,
        ...columnsData.map((row) => {
          const value = row[header];
          return typeof value === "string" || typeof value === "number"
            ? value.toString().length
            : 0;
        }),
      );
      return { width: maxLength + 2 }; // Add padding
    });
  }
}

export const useExcelController = createControllerHook(ExcelController);

const EXCEL_PAGE_SIZE = 200;

const ACTIVITY_STATUS_LABELS: Record<string, string> = {
  NEW: "חדש",
  ACTIVE: "פעיל",
  INACTIVE: "לא פעיל",
  LEGACY: "מאגר ישן",
};

const MEMBER_EXPORT_HEADERS = [
  "שם מלא",
  "מספר טלפון",
  "אימייל",
  "מגדר",
  "גיל",
  "תאריך הצטרפות לקהילה",
  "סטטוס פעילות",
  "מוביל קהילה",
  "מספר התנדבויות מאושרות",
  "תאריך התנדבות אחרון",
  "שעות מדווחות",
];

const ACTIVITY_EXPORT_HEADERS = [
  "שם מלא",
  "מספר טלפון",
  "משימה",
  "תאריך",
  "תת-קטגוריה",
  "גודל קבוצה",
];

function safeExportName(name?: string) {
  const cleaned = (name ?? "")
    .replace(/[\\/:*?"<>|]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  return cleaned.slice(0, 40) || "קהילה";
}

function formatExportDate(value: Date | string | null | undefined) {
  if (!value) return "";
  const date = dayjs(value);
  if (!date.isValid()) return "";
  return date.format("DD/MM/YYYY");
}

function formatExportAge(dateOfBirth: Date | string | null | undefined) {
  if (!dateOfBirth) return "";
  const dob = dayjs(dateOfBirth);
  if (!dob.isValid()) return "";
  const age = dayjs().diff(dob, "year");
  if (age < 0 || age > 120) return "";
  return String(age);
}

async function findAllPages<T>(loadPage: (page: number) => Promise<T[]>) {
  const all: T[] = [];
  for (let page = 1; page < 500; page += 1) {
    const batch = await loadPage(page);
    all.push(...batch);
    if (batch.length < EXCEL_PAGE_SIZE) return all;
  }
  return all;
}

function chunkIds(ids: string[], size = 100) {
  const chunks: string[][] = [];
  for (let index = 0; index < ids.length; index += size) {
    chunks.push(ids.slice(index, index + size));
  }
  return chunks;
}

async function loadCommunityMemberActivity(associationIds: string[]) {
  const byId = new Map<
    string,
    { approvedCount: number; lastAt: Date | null; hours: number }
  >();
  for (const id of associationIds) {
    byId.set(id, { approvedCount: 0, lastAt: null, hours: 0 });
  }
  if (associationIds.length === 0) return byId;

  for (const ids of chunkIds(associationIds)) {
    const [participants, reports] = await Promise.all([
      findAllPages((page) =>
        repo(OpportunityParticipant).find({
          where: OpportunityParticipant.hyperFilter({
            associationId: { $in: ids },
            status: "APPROVED",
            opportunity: {
              $is: {
                isDeleted: false,
                status: { $ne: "AID_REQUEST" },
              },
            },
          }),
          include: { group: true },
          limit: EXCEL_PAGE_SIZE,
          page,
        }),
      ),
      findAllPages((page) =>
        repo(TimeReport).find({
          where: TimeReport.hyperFilter({ associationId: { $in: ids } }),
          limit: EXCEL_PAGE_SIZE,
          page,
        }),
      ),
    ]);

    for (const participant of participants) {
      const current = byId.get(participant.associationId);
      if (!current) continue;
      current.approvedCount += resolveParticipantMatchWeight({
        matchWeight: participant.matchWeight,
        groupId: participant.groupId,
        groupSize: participant.group?.size,
      });
      const at = participant.matchedAt ?? participant.createdAt;
      if (at && (!current.lastAt || new Date(at) > current.lastAt)) {
        current.lastAt = new Date(at);
      }
    }
    for (const report of reports) {
      const current = byId.get(report.associationId);
      if (!current) continue;
      current.hours += Number(report.hourCount) || 0;
    }
  }

  for (const current of byId.values()) {
    current.hours = Math.round(current.hours * 100) / 100;
  }
  return byId;
}

async function loadCommunityActivityRows(
  associationIds: string[],
  members: CommunityAssociation[],
) {
  const memberByAssociation = new Map(
    members.map((member) => [member.associationId, member]),
  );
  const rows: { at: number; row: ExportRow }[] = [];
  for (const ids of chunkIds(associationIds)) {
    const participants = await findAllPages((page) =>
      repo(OpportunityParticipant).find({
        where: OpportunityParticipant.hyperFilter({
          associationId: { $in: ids },
          status: "APPROVED",
          opportunity: {
            $is: {
              isDeleted: false,
              status: { $ne: "AID_REQUEST" },
            },
          },
        }),
        include: {
          opportunity: { include: { subCategory: true } },
          group: true,
        },
        limit: EXCEL_PAGE_SIZE,
        page,
      }),
    );
    for (const participant of participants) {
      const member = memberByAssociation.get(participant.associationId);
      const user = member?.association?.user;
      const opportunity = participant.opportunity;
      const at =
        opportunity?.startDate ?? participant.matchedAt ?? participant.createdAt;
      rows.push({
        at: at ? new Date(at).getTime() : 0,
        row: {
          "שם מלא": user?.name || "",
          "מספר טלפון": user?.phoneNumber || "",
          משימה: opportunity?.title || "",
          תאריך: formatExportDate(at),
          "תת-קטגוריה": opportunity?.subCategory?.name || "ללא קטגוריה",
          "גודל קבוצה": participant.group?.size ?? "",
        },
      });
    }
  }
  rows.sort((left, right) => right.at - left.at);
  return rows.map((entry) => entry.row);
}
